/** StereoDelay — Web Audio API delay effect with feedback, filtering,
 * ping-pong mode, and tempo sync.
 *
 * Audio chain:
 *   input → dryGain ────────────────────────────┐
 *   input → wetGain → delayL / delayR → filter → ├→ merger → output
 *                   ↖ feedback ←──────────────────┘
 *
 * In ping-pong mode, left and right delay lines cross-feed into each other.
 */

import type { DelayParams, NoteDivision } from "../types/synth.ts";
import {
	DEFAULT_DELAY,
	DEFAULT_PARAMS,
	NOTE_DIVISION_MULTIPLIERS,
} from "../types/synth.ts";

/** Calculate delay time in seconds from BPM and note division. */
function tempoSyncedTime(bpm: number, division: NoteDivision): number {
	if (!Number.isFinite(bpm) || bpm <= 0) {
		return DEFAULT_DELAY.timeMs / 1000;
	}
	const multiplier =
		NOTE_DIVISION_MULTIPLIERS[division] ??
		NOTE_DIVISION_MULTIPLIERS[DEFAULT_DELAY.noteDivision];
	const beatDuration = 60 / bpm;
	return beatDuration * multiplier;
}

function clampFinite(
	value: number,
	min: number,
	max: number,
	fallback: number,
): number {
	if (!Number.isFinite(value)) return fallback;
	return Math.min(Math.max(value, min), max);
}

/** Maximum delay time in seconds. */
const MAX_DELAY_TIME = 5 as const;

/** The stereo delay effect processor. */
export class StereoDelay {
	private readonly context: AudioContext;
	private readonly inputNode: GainNode;
	private readonly outputNode: GainNode;
	private readonly dryGain: GainNode;
	private readonly wetGain: GainNode;
	private readonly delayL: DelayNode;
	private readonly delayR: DelayNode;
	private readonly feedbackL: GainNode;
	private readonly feedbackR: GainNode;
	private readonly filterL: BiquadFilterNode;
	private readonly filterR: BiquadFilterNode;
	private readonly splitter: ChannelSplitterNode;
	private readonly merger: ChannelMergerNode;
	private params: DelayParams;
	private bpm: number;

	constructor(context: AudioContext, bpm = 120) {
		this.context = context;
		this.bpm = bpm;
		this.params = { ...DEFAULT_DELAY };

		// Create nodes
		this.inputNode = context.createGain();
		this.outputNode = context.createGain();
		this.dryGain = context.createGain();
		this.wetGain = context.createGain();
		this.delayL = context.createDelay(MAX_DELAY_TIME);
		this.delayR = context.createDelay(MAX_DELAY_TIME);
		this.feedbackL = context.createGain();
		this.feedbackR = context.createGain();
		this.filterL = context.createBiquadFilter();
		this.filterR = context.createBiquadFilter();
		this.splitter = context.createChannelSplitter(2);
		this.merger = context.createChannelMerger(2);

		// Configure filters
		this.filterL.type = "lowpass";
		this.filterR.type = "lowpass";

		// Set initial parameters
		this.applyParams();

		// Wire the graph (standard stereo delay)
		this.wireGraph();
	}

	/** The node to connect audio sources into. */
	get input(): GainNode {
		return this.inputNode;
	}

	/** The node to connect to downstream destinations. */
	get output(): GainNode {
		return this.outputNode;
	}

	/** Wire (or re-wire) the internal audio graph. */
	private wireGraph(): void {
		// Disconnect everything first
		this.inputNode.disconnect();
		this.dryGain.disconnect();
		this.wetGain.disconnect();
		this.delayL.disconnect();
		this.delayR.disconnect();
		this.feedbackL.disconnect();
		this.feedbackR.disconnect();
		this.filterL.disconnect();
		this.filterR.disconnect();
		this.splitter.disconnect();
		this.merger.disconnect();

		// Dry path: input → dryGain → output
		this.inputNode.connect(this.dryGain);
		this.dryGain.connect(this.outputNode);

		if (this.params.pingPong) {
			// Ping-pong: input splits to L channel, feeds through delay lines alternately
			// input → splitter
			this.inputNode.connect(this.splitter);

			// L channel → delayL → filterL → feedbackL → delayR
			this.splitter.connect(this.delayL, 0);
			this.splitter.connect(this.delayL, 1); // mix both channels into L
			this.delayL.connect(this.filterL);
			this.filterL.connect(this.feedbackL);
			this.feedbackL.connect(this.delayR);

			// delayR → filterR → feedbackR → delayL (cross-feed)
			this.delayR.connect(this.filterR);
			this.filterR.connect(this.feedbackR);
			this.feedbackR.connect(this.delayL);

			// Merge: filterL → left, filterR → right
			this.filterL.connect(this.merger, 0, 0);
			this.filterR.connect(this.merger, 0, 1);
			this.merger.connect(this.wetGain);
			this.wetGain.connect(this.outputNode);
		} else {
			// Standard stereo delay: both channels through same delay time
			this.inputNode.connect(this.delayL);
			this.inputNode.connect(this.delayR);

			this.delayL.connect(this.filterL);
			this.delayR.connect(this.filterR);

			this.filterL.connect(this.feedbackL);
			this.filterR.connect(this.feedbackR);

			this.feedbackL.connect(this.delayL);
			this.feedbackR.connect(this.delayR);

			this.filterL.connect(this.merger, 0, 0);
			this.filterR.connect(this.merger, 0, 1);
			this.merger.connect(this.wetGain);
			this.wetGain.connect(this.outputNode);
		}
	}

	/** Apply all current params to the audio nodes. */
	private applyParams(): void {
		const now = this.context.currentTime;
		const p = this.params;
		const bpm =
			Number.isFinite(this.bpm) && this.bpm > 0
				? this.bpm
				: DEFAULT_PARAMS.tempo;
		const enabled = p.enabled;

		// Delay time
		const time =
			p.timeMode === "sync"
				? tempoSyncedTime(bpm, p.noteDivision)
				: clampFinite(
						p.timeMs,
						0,
						MAX_DELAY_TIME * 1000,
						DEFAULT_DELAY.timeMs,
					) / 1000;
		const clampedTime = Math.min(time, MAX_DELAY_TIME);

		this.delayL.delayTime.setValueAtTime(clampedTime, now);
		this.delayR.delayTime.setValueAtTime(clampedTime, now);

		// Feedback
		const feedback = enabled
			? clampFinite(p.feedback, 0, 0.95, DEFAULT_DELAY.feedback)
			: 0;
		this.feedbackL.gain.setValueAtTime(feedback, now);
		this.feedbackR.gain.setValueAtTime(feedback, now);

		// Mix (dry/wet)
		const mix = enabled ? clampFinite(p.mix, 0, 1, DEFAULT_DELAY.mix) : 0;
		this.dryGain.gain.setValueAtTime(enabled ? 1 - mix : 1, now);
		this.wetGain.gain.setValueAtTime(mix, now);

		// Filter
		const cutoff = clampFinite(
			p.filterCutoff,
			20,
			20000,
			DEFAULT_DELAY.filterCutoff,
		);
		this.filterL.frequency.setValueAtTime(cutoff, now);
		this.filterR.frequency.setValueAtTime(cutoff, now);
	}

	/** Update delay parameters. Re-wires graph if ping-pong changes. */
	setParams(update: Partial<DelayParams>): void {
		const prevPingPong = this.params.pingPong;
		this.params = { ...this.params, ...update };

		if (update.pingPong !== undefined && update.pingPong !== prevPingPong) {
			this.wireGraph();
		}

		this.applyParams();
	}

	/** Update the global BPM — recalculates delay time if tempo-synced. */
	setTempo(bpm: number): void {
		this.bpm = bpm;
		if (this.params.timeMode === "sync") {
			this.applyParams();
		}
	}

	/** Whether the delay effect is currently enabled. */
	get enabled(): boolean {
		return this.params.enabled;
	}

	/** Current delay parameters. */
	get currentParams(): Readonly<DelayParams> {
		return this.params;
	}

	/** Get the effective delay time in milliseconds. */
	get effectiveTimeMs(): number {
		if (this.params.timeMode === "sync") {
			return tempoSyncedTime(this.bpm, this.params.noteDivision) * 1000;
		}
		return this.params.timeMs;
	}

	/** Clean up all audio nodes. */
	destroy(): void {
		try {
			this.inputNode.disconnect();
			this.outputNode.disconnect();
			this.dryGain.disconnect();
			this.wetGain.disconnect();
			this.delayL.disconnect();
			this.delayR.disconnect();
			this.feedbackL.disconnect();
			this.feedbackR.disconnect();
			this.filterL.disconnect();
			this.filterR.disconnect();
			this.splitter.disconnect();
			this.merger.disconnect();
		} catch {
			// Already disconnected
		}
	}
}
